import React, { CSSProperties, useEffect, useRef } from 'react';
import { StyleProp, View, ViewStyle } from 'react-native';
import { translate } from '../../services/translate';
import MyAppText from '../MyAppText';

interface PlateImageProps {
    src: string | null;
    platePosition?: { x: number; y: number; }[];
    style?: CSSProperties | undefined;
    viewStyle?: StyleProp<ViewStyle>;
    width?: number;
    height?: number;
}

export default function PlateImage({ style, viewStyle, src, platePosition, width, height }: PlateImageProps) {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    const canvasWidth = width || 100;
    const canvasHeight = height || 50;

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) {
            return;
        }

        const ctx = canvas.getContext('2d');
        if (!ctx || !src || !platePosition) {
            return;
        }

        const image = new Image();

        image.onload = () => {
            ctx.clearRect(0, 0, canvasWidth, canvasHeight);

            if (!Array.isArray(platePosition) || platePosition.length === 0) {
                // No plate → draw full image and exit
                const scale = Math.min(canvasWidth / image.naturalWidth, canvasHeight / image.naturalHeight);
                const drawWidth = image.naturalWidth * scale;
                const drawHeight = image.naturalHeight * scale;
                const offsetX = (canvasWidth - drawWidth) / 2;
                const offsetY = (canvasHeight - drawHeight) / 2;

                ctx.drawImage(image, offsetX, offsetY, drawWidth, drawHeight);
                return;
            }

            // Plate exists → crop
            const rect = {
                minX: Infinity,
                maxX: 0,
                minY: Infinity,
                maxY: 0
            };

            for (const position of platePosition) {
                if (position.x < rect.minX) {
                    rect.minX = position.x;
                }
                if (position.x > rect.maxX) {
                    rect.maxX = position.x;
                }
                if (position.y < rect.minY) {
                    rect.minY = position.y;
                }
                if (position.y > rect.maxY) {
                    rect.maxY = position.y;
                }
            }

            const resizeImageBy = Math.min(canvasWidth / (rect.maxX - rect.minX), canvasHeight / (rect.maxY - rect.minY));
            image.width *= resizeImageBy;
            image.height *= resizeImageBy;

            const xRatio = image.width / image.naturalWidth;
            const yRatio = image.height / image.naturalHeight;

            ctx.drawImage(
                image,
                rect.minX * xRatio * -1,
                rect.minY * yRatio * -1,
                image.width,
                image.height
            );
        };

        image.src = src;
    }, [src]);

    return (
        <View style={[{ width: canvasWidth, height: canvasHeight, justifyContent: 'center', alignContent: 'center' }, viewStyle]}>
            {platePosition ?
                <canvas style={style} width={canvasWidth} height={canvasHeight} ref={canvasRef}></canvas>
                : <MyAppText style={{ textAlign: 'center', color: '#f00' }}>{translate('noImage')}{platePosition}</MyAppText>}
        </View>
    );
}
